import { useState, useEffect, useMemo } from "react";
import { X, Plus, Loader2, Radio, Zap, Target, Package, Users } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useAuth, canEditOpportunities } from "@/lib/auth";
import { unwrapCompaniesPage } from "@/lib/companies";
import { GATES, GATE_LABELS, MONEY_UNIT_LABEL } from "@/lib/utils-ros";

/**
 * QuickAdd — global "create anywhere, at any stage" modal.
 * User picks entity type (Signal / Lead / Opportunity / Order / Partner) and can either
 * pick an existing company or type a brand-new company name (created inline).
 */
const BASE_ENTITIES = [
  { key: "signal", label: "Signal", icon: Radio, desc: "Capture buying intent" },
  { key: "lead", label: "Lead", icon: Zap, desc: "Qualify an inbound / referral" },
  { key: "opportunity", label: "Opportunity", icon: Target, desc: "Enter at ANY gate — even Pilot" },
  { key: "order", label: "Order", icon: Package, desc: "Direct booking / manual PO" },
];
const PARTNER_ENTITY = { key: "partner", label: "Partner", icon: Users, desc: "Add a partner firm + user" };
const CONTACT_ROLES = [
  ["unknown", "Contact role"], ["economic_buyer", "Economic Buyer"],
  ["power_sponsor", "Power Sponsor"], ["champion", "Champion"],
  ["technical_evaluator", "Technical Evaluator"], ["operations", "Operations"],
  ["finance", "Finance"], ["procurement", "Procurement"], ["it", "IT"],
  ["legal", "Legal"], ["influencer", "Influencer"], ["user", "User"],
];

export function QuickAdd({ open, onOpenChange }) {
  const { user, role, canWrite } = useAuth();
  const canManagePartners = role === "super_admin" || role === "admin" || role === "founder" || role === "growth_marketing";
  const ENTITIES = useMemo(() => {
    const list = BASE_ENTITIES.filter(entity => {
      if (entity.key === "signal") return canWrite("signals");
      if (entity.key === "lead") return canWrite("leads");
      if (entity.key === "opportunity") return canEditOpportunities(user);
      if (entity.key === "order") return canWrite("orders");
      return true;
    });
    return canManagePartners ? [...list, PARTNER_ENTITY] : list;
  }, [canManagePartners, canWrite, user]);
  const [entityType, setEntityType] = useState("opportunity");

  useEffect(() => {
    if (!open || ENTITIES.length === 0) return;
    if (!ENTITIES.some((entity) => entity.key === entityType)) {
      setEntityType(ENTITIES[0].key);
    }
  }, [open, ENTITIES, entityType]);

  const [companies, setCompanies] = useState([]);
  const [companyMode, setCompanyMode] = useState("existing"); // existing | new
  const [companyId, setCompanyId] = useState("");
  const [newCompany, setNewCompany] = useState({ name: "", industry: "", revenue_cr: "" });
  const [payload, setPayload] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    if (open) {
      api.get("/companies", { params: { limit: 200 } }).then(r => setCompanies(unwrapCompaniesPage(r.data).items)).catch(() => {});
    }
  }, [open]);

  useEffect(() => {
    // reset payload when entity changes
    if (entityType === "opportunity") setPayload({ current_gate: "pilot_commercial", value_cr: 100, cost_cr: 40, probability: 40 });
    else if (entityType === "signal") setPayload({ source: "manual", signal_type: "manual_entry", strength: 60, intent: "" });
    else if (entityType === "lead") setPayload({ urgency: "medium", contact_name: "", contact_role: "unknown", pain_point: "", product_interest: "" });
    else if (entityType === "order") setPayload({ po_number: "", value_cr: 100, expected_delivery: "" });
    else if (entityType === "partner") setPayload({
      firm: "", name: "", region: "North India", email: "", phone: "", pmm_level: "explorer",
      user_email: "", user_name: "", user_password: "",
    });
  }, [entityType]);

  useEffect(() => {
    if (open) {
      setCompanyMode("existing");
      setCompanyId("");
      setNewCompany({ name: "", industry: "", revenue_cr: "" });
    }
  }, [open]);

  const companyOptions = useMemo(
    () => companies.slice().sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [companies]
  );

  async function submit() {
    // ---- Partner path (no company) ----
    if (entityType === "partner") {
      if (!payload.firm?.trim() || !payload.name?.trim()) { toast.error("Firm name & primary contact are required"); return; }
      const body = {
        firm: payload.firm.trim(), name: payload.name.trim(),
        region: payload.region || "North India",
        email: payload.email?.trim() || null,
        phone: payload.phone?.trim() || null,
        pmm_level: payload.pmm_level || "explorer",
        users: (payload.user_email?.trim() && payload.user_name?.trim()) ? [{
          email: payload.user_email.trim(),
          name: payload.user_name.trim(),
          password: payload.user_password || undefined,
        }] : [],
      };
      setSubmitting(true);
      try {
        const { data } = await api.post("/partners", body);
        toast.success(`${data.partner.firm} added${data.users?.length ? ` · ${data.users.length} user(s) invited` : ""}`);
        onOpenChange(false);
        nav("/partner-management");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Failed to create partner");
      } finally { setSubmitting(false); }
      return;
    }

    const cleanPayload = { ...payload };
    // coerce numbers
    ["value_cr", "cost_cr", "probability", "estimated_value_cr", "strength"].forEach(k => {
      if (cleanPayload[k] !== undefined && cleanPayload[k] !== "") cleanPayload[k] = Number(cleanPayload[k]);
    });
    if (entityType === "lead" && typeof cleanPayload.product_interest === "string") {
      cleanPayload.product_interest = cleanPayload.product_interest.split(",").map(value => value.trim()).filter(Boolean);
    }
    if (entityType === "signal" && !cleanPayload.intent) {
      toast.error("Intent / event description is required");
      return;
    }
    const body = { entity_type: entityType, payload: cleanPayload };
    if (companyMode === "existing") {
      if (!companyId) { toast.error("Please pick a company"); return; }
      body.company_id = companyId;
    } else {
      if (!newCompany.name.trim()) { toast.error("Company name is required"); return; }
      body.company = {
        name: newCompany.name.trim(),
        industry: newCompany.industry || "Manufacturing",
        revenue_cr: newCompany.revenue_cr ? Number(newCompany.revenue_cr) : null,
      };
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/quick-add", body);
      toast.success(`${ENTITIES.find(e => e.key === entityType).label} created`);
      onOpenChange(false);
      // Navigate to relevant list
      const routes = { signal: "/signals", lead: "/leads", opportunity: `/opportunities/${data.id}`, order: "/orders" };
      nav(routes[entityType] || "/");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create — check inputs");
    } finally { setSubmitting(false); }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4 md:p-6 overflow-y-auto" onClick={() => onOpenChange(false)} data-testid="quick-add-modal">
      <div
        className="w-full max-w-3xl bg-[#EFF8FC] border border-[#BFD9E5] rounded-[26px] shadow-[0_28px_70px_rgba(7,83,154,0.22)] flex flex-col max-h-[90vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 md:px-6 py-5 border-b border-[#D5E7EF]">
          <span className="w-9 h-9 rounded-xl bg-white text-[#07539A] flex items-center justify-center shadow-[0_3px_10px_rgba(7,83,154,0.07)]"><Plus size={16} /></span>
          <div className="flex-1">
            <div className="font-display text-xl text-[#07539A]">Quick Add</div>
            <div className="text-[11px] text-[#607D8B] mt-0.5">Create a record at any stage of the revenue journey.</div>
          </div>
          <button onClick={() => onOpenChange(false)} data-testid="quick-add-close" aria-label="Close Quick Add" className="w-8 h-8 rounded-full text-[#607D8B] hover:bg-white hover:text-[#07539A] flex items-center justify-center transition-colors"><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 md:p-6 space-y-5 [&_input]:bg-white [&_input]:border-[#B8D3DF] [&_input]:rounded-xl [&_input]:h-11 [&_input]:px-3.5 [&_input]:outline-none [&_input]:shadow-[0_4px_12px_rgba(7,83,154,0.06)] [&_input:focus]:border-[#00A2D4] [&_select]:bg-white [&_select]:border-[#B8D3DF] [&_select]:rounded-xl [&_select]:h-11 [&_select]:px-3.5 [&_select]:outline-none [&_select]:shadow-[0_4px_12px_rgba(7,83,154,0.06)] [&_select:focus]:border-[#00A2D4]">
          {/* Entity type picker */}
          <div>
            <div className="text-[13px] font-semibold text-[#063F73] mb-2.5">What would you like to add?</div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="qa-entity-picker">
            {ENTITIES.map(e => (
              <button
                key={e.key}
                onClick={() => setEntityType(e.key)}
                data-testid={`qa-type-${e.key}`}
                className={`text-left p-3 rounded-xl border shadow-[0_3px_10px_rgba(7,83,154,0.04)] transition-all ${
                  entityType === e.key
                    ? "border-[#07539A] bg-[#E3F2F8] shadow-[0_5px_14px_rgba(7,83,154,0.10)]"
                    : "border-[#D4E3EA] bg-white hover:border-[#91C6DB] hover:bg-[#F8FCFE]"
                }`}
              >
                <e.icon size={15} className={entityType === e.key ? "text-[#07539A]" : "text-[#607D8B]"} />
                <div className="text-[13px] font-semibold text-[#102A43] mt-1.5">{e.label}</div>
                <div className="text-[10px] text-[#78909C] mt-0.5 leading-tight">{e.desc}</div>
              </button>
            ))}
            </div>
          </div>

          {/* Company selector — not shown for Partner type */}
          {entityType !== "partner" && (
          <div className="space-y-2.5">
            <div className="text-[13px] font-semibold text-[#063F73]">Company</div>
            <div className="flex gap-1 border border-[#C9DCE6] rounded-full p-1 bg-white w-fit shadow-[0_3px_10px_rgba(7,83,154,0.04)]">
              <button
                onClick={() => setCompanyMode("existing")}
                data-testid="qa-company-existing"
                className={`px-3.5 h-8 text-[11px] rounded-full transition-colors ${companyMode === "existing" ? "bg-[#07539A] text-[#FFFFFF]" : "text-[#607D8B] hover:bg-[#EDF7FB]"}`}
              >Existing</button>
              <button
                onClick={() => setCompanyMode("new")}
                data-testid="qa-company-new"
                className={`px-3.5 h-8 text-[11px] rounded-full transition-colors ${companyMode === "new" ? "bg-[#07539A] text-[#FFFFFF]" : "text-[#607D8B] hover:bg-[#EDF7FB]"}`}
              >+ New Company</button>
            </div>
            {companyMode === "existing" ? (
              <select
                value={companyId}
                onChange={e => setCompanyId(e.target.value)}
                data-testid="qa-company-select"
                className="w-full h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]"
              >
                <option value="">— Pick a company —</option>
                {companyOptions.map(c => (
                  <option key={c.id} value={c.id}>{c.name} · {c.industry}</option>
                ))}
              </select>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <div>
                  <label htmlFor="qa-new-company-name" className="block text-[11px] font-semibold text-[#455A64] mb-1.5">Company name</label>
                  <input
                    id="qa-new-company-name"
                    placeholder="Company name"
                    value={newCompany.name}
                    onChange={e => setNewCompany({ ...newCompany, name: e.target.value })}
                    data-testid="qa-new-company-name"
                    className="w-full h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]"
                  />
                </div>
                <div>
                  <label htmlFor="qa-new-company-industry" className="block text-[11px] font-semibold text-[#455A64] mb-1.5">Industry</label>
                  <input
                    id="qa-new-company-industry"
                    placeholder="Industry"
                    value={newCompany.industry}
                    onChange={e => setNewCompany({ ...newCompany, industry: e.target.value })}
                    data-testid="qa-new-company-industry"
                    className="w-full h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]"
                  />
                </div>
                <div>
                  <label htmlFor="qa-new-company-revenue" className="block text-[11px] font-semibold text-[#455A64] mb-1.5">Annual revenue ({MONEY_UNIT_LABEL})</label>
                  <input
                    id="qa-new-company-revenue"
                    placeholder={`Annual revenue (${MONEY_UNIT_LABEL})`}
                    type="number"
                    value={newCompany.revenue_cr}
                    onChange={e => setNewCompany({ ...newCompany, revenue_cr: e.target.value })}
                    data-testid="qa-new-company-revenue"
                    className="w-full h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]"
                  />
                </div>
              </div>
            )}
          </div>
          )}

          {/* Entity-specific fields */}
          <div className="border-t border-[#D5E7EF] pt-5 space-y-3" data-testid={`qa-fields-${entityType}`}>
            <div className="text-[13px] font-semibold text-[#063F73]">Details</div>

            {entityType === "partner" && (
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <input placeholder="Firm name *" value={payload.firm || ""} onChange={e => setPayload({ ...payload, firm: e.target.value })} data-testid="qa-partner-firm" className="h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]" />
                  <input placeholder="Primary contact *" value={payload.name || ""} onChange={e => setPayload({ ...payload, name: e.target.value })} data-testid="qa-partner-name" className="h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]" />
                  <input placeholder="Region" value={payload.region || ""} onChange={e => setPayload({ ...payload, region: e.target.value })} data-testid="qa-partner-region" className="h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]" />
                  <select value={payload.pmm_level || "explorer"} onChange={e => setPayload({ ...payload, pmm_level: e.target.value })} data-testid="qa-partner-pmm" className="h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]">
                    {["explorer", "practitioner", "specialist", "growth_partner", "strategic_partner"].map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                  <input placeholder="Firm email" value={payload.email || ""} onChange={e => setPayload({ ...payload, email: e.target.value })} data-testid="qa-partner-email" className="h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]" />
                  <input placeholder="Phone" value={payload.phone || ""} onChange={e => setPayload({ ...payload, phone: e.target.value })} data-testid="qa-partner-phone" className="h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]" />
                </div>
                <div className="pt-3 border-t border-[#D5E7EF]">
                  <div className="text-[11px] font-semibold text-[#07539A] mb-2">Invite first partner user <span className="font-normal text-[#78909C]">(optional)</span></div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                    <input placeholder="User name" value={payload.user_name || ""} onChange={e => setPayload({ ...payload, user_name: e.target.value })} data-testid="qa-partner-user-name" className="h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]" />
                    <input placeholder="User email" value={payload.user_email || ""} onChange={e => setPayload({ ...payload, user_email: e.target.value })} data-testid="qa-partner-user-email" className="h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]" />
                    <input placeholder="Password (blank → auto-generated)" value={payload.user_password || ""} onChange={e => setPayload({ ...payload, user_password: e.target.value })} data-testid="qa-partner-user-password" className="h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]" />
                  </div>
                </div>
              </div>
            )}
            {entityType === "signal" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input placeholder="Intent / event (required)" value={payload.intent || ""} onChange={e => setPayload({ ...payload, intent: e.target.value })} data-testid="qa-signal-intent" className="md:col-span-2 h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]" />
                <select value={payload.source} onChange={e => setPayload({ ...payload, source: e.target.value })} data-testid="qa-signal-source" className="h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]">
                  <option value="manual">Manual</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="website">Website</option>
                  <option value="news">News</option>
                  <option value="tender">Tender</option>
                  <option value="referral">Referral</option>
                </select>
                <input type="number" placeholder="Strength (0-100)" value={payload.strength || ""} onChange={e => setPayload({ ...payload, strength: e.target.value })} data-testid="qa-signal-strength" className="h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]" />
              </div>
            )}

            {entityType === "lead" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input placeholder="Primary lead contact" value={payload.contact_name || ""} onChange={e => setPayload({ ...payload, contact_name: e.target.value })} data-testid="qa-lead-contact" className="h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]" />
                <input placeholder="Primary contact title" value={payload.contact_title || ""} onChange={e => setPayload({ ...payload, contact_title: e.target.value })} data-testid="qa-lead-title" className="h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]" />
                <select value={payload.contact_role || "unknown"} onChange={e => setPayload({ ...payload, contact_role: e.target.value })} data-testid="qa-lead-contact-role" className="md:col-span-2 h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]">
                  {CONTACT_ROLES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                </select>
                <input placeholder="Pain point" value={payload.pain_point || ""} onChange={e => setPayload({ ...payload, pain_point: e.target.value })} data-testid="qa-lead-pain" className="md:col-span-2 h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]" />
                <input placeholder="Interested products (comma-separated)" value={payload.product_interest || ""} onChange={e => setPayload({ ...payload, product_interest: e.target.value })} data-testid="qa-lead-products" className="md:col-span-2 h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]" />
                <select value={payload.urgency} onChange={e => setPayload({ ...payload, urgency: e.target.value })} data-testid="qa-lead-urgency" className="h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]">
                  <option value="low">Low urgency</option>
                  <option value="medium">Medium urgency</option>
                  <option value="high">High urgency</option>
                  <option value="urgent">Urgent</option>
                </select>
                <input type="number" step="0.01" placeholder={`Est. value (${MONEY_UNIT_LABEL})`} value={payload.estimated_value_cr || ""} onChange={e => setPayload({ ...payload, estimated_value_cr: e.target.value })} data-testid="qa-lead-value" className="h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]" />
              </div>
            )}

            {entityType === "opportunity" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <label htmlFor="qa-opp-name" className="block text-[11px] font-semibold text-[#455A64] mb-1.5">Deal name</label>
                  <input id="qa-opp-name" placeholder="e.g. Plant-wide energy rollout" value={payload.name || ""} onChange={e => setPayload({ ...payload, name: e.target.value })} data-testid="qa-opp-name" className="w-full h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]" />
                </div>
                <div className="md:col-span-2">
                  <div className="text-[11px] font-semibold text-[#455A64] mb-2">Enter at gate</div>
                  <div className="flex flex-wrap gap-1.5">
                    {GATES.map((g, i) => (
                      <button
                        key={g}
                        onClick={() => setPayload({ ...payload, current_gate: g })}
                        data-testid={`qa-opp-gate-${g}`}
                        className={`text-[11px] px-3 py-1.5 rounded-lg border transition-colors ${payload.current_gate === g ? "bg-[#07539A] border-[#07539A] text-[#FFFFFF]" : "bg-white border-[#C9DCE6] text-[#607D8B] hover:border-[#00A2D4] hover:text-[#07539A]"}`}
                      >G{i + 1} · {GATE_LABELS[g]}</button>
                    ))}
                  </div>
                </div>
                <div>
                  <label htmlFor="qa-opp-value" className="block text-[11px] font-semibold text-[#455A64] mb-1.5">Deal value ({MONEY_UNIT_LABEL})</label>
                  <input id="qa-opp-value" type="number" step="0.01" value={payload.value_cr || ""} onChange={e => setPayload({ ...payload, value_cr: e.target.value })} data-testid="qa-opp-value" className="w-full h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]" />
                </div>
                <div>
                  <label htmlFor="qa-opp-cost" className="block text-[11px] font-semibold text-[#455A64] mb-1.5">Cost to serve ({MONEY_UNIT_LABEL})</label>
                  <input id="qa-opp-cost" type="number" step="0.01" value={payload.cost_cr || ""} onChange={e => setPayload({ ...payload, cost_cr: e.target.value })} data-testid="qa-opp-cost" className="w-full h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]" />
                </div>
                <div>
                  <label htmlFor="qa-opp-prob" className="block text-[11px] font-semibold text-[#455A64] mb-1.5">Win probability (%)</label>
                  <input id="qa-opp-prob" type="number" min="0" max="100" value={payload.probability || ""} onChange={e => setPayload({ ...payload, probability: e.target.value })} data-testid="qa-opp-prob" className="w-full h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]" />
                </div>
                <div>
                  <label htmlFor="qa-opp-products" className="block text-[11px] font-semibold text-[#455A64] mb-1.5">Products</label>
                  <input id="qa-opp-products" placeholder="Comma separated" onChange={e => setPayload({ ...payload, products: e.target.value.split(",").map(x => x.trim()).filter(Boolean) })} data-testid="qa-opp-products" className="w-full h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]" />
                </div>
              </div>
            )}

            {entityType === "order" && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <input placeholder="PO number (auto if blank)" value={payload.po_number || ""} onChange={e => setPayload({ ...payload, po_number: e.target.value })} data-testid="qa-order-po" className="h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]" />
                <input type="number" step="0.01" placeholder={`Value (${MONEY_UNIT_LABEL})`} value={payload.value_cr || ""} onChange={e => setPayload({ ...payload, value_cr: e.target.value })} data-testid="qa-order-value" className="h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]" />
                <input type="date" placeholder="Expected delivery" value={payload.expected_delivery || ""} onChange={e => setPayload({ ...payload, expected_delivery: e.target.value })} data-testid="qa-order-delivery" className="h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]" />
                <input placeholder="Products (comma-sep)" onChange={e => setPayload({ ...payload, products: e.target.value.split(",").map(x => x.trim()).filter(Boolean) })} data-testid="qa-order-products" className="h-9 bg-white/[0.03] border border-white/10 rounded-md px-3 text-[13px]" />
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 md:px-6 py-4 border-t border-[#D5E7EF] bg-[#EFF8FC]">
          <button onClick={() => onOpenChange(false)} data-testid="qa-cancel" className="h-10 px-4 rounded-xl text-[13px] text-[#607D8B] hover:bg-white hover:text-[#07539A] transition-colors">Cancel</button>
          <button
            onClick={submit}
            disabled={submitting}
            data-testid="qa-submit"
            className="h-10 px-5 rounded-xl bg-[#07539A] hover:bg-[#063F73] text-[#FFFFFF] text-[13px] font-medium shadow-[0_8px_18px_rgba(7,83,154,0.22)] flex items-center gap-2 disabled:opacity-50 transition-all"
          >
            {submitting ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
            Create {ENTITIES.find(e => e.key === entityType).label}
          </button>
        </div>
      </div>
    </div>
  );
}
